import type { ReactNode } from 'react'
import {
  Stack, Group, Button, Table, Badge, ActionIcon, Pagination, Center, Loader, Text, Alert, Tooltip,
} from '@mantine/core'
import { IconEye, IconAlertCircle } from '@tabler/icons-react'
import { operationColor } from './auditFormat'
import { errorKind, errorSummary } from '../../utils/format'
import type { PagedResponse } from '../../types/api'
import type { AuditLogSummary } from '../../types/auditLog'

/** What the trail says when the server answered with no rows on page 1. */
export const NO_AUDIT_ENTRIES_MATCH = 'No audit entries match the current filters.'

/**
 * One page of the audit trail as a table: loading, failed, rows, empty, past the
 * end, and the pager — every state of it, and nothing about how it was asked.
 *
 * Extracted from pages/audit/AuditLogPage.tsx with NO behaviour change, so the
 * Audit Log page and Report Detail's History tab render the trail one way. The
 * caller owns the query (and so the gate and the key), the page number, and the
 * detail modal; this owns the four-way partition below, which is the part that
 * must not drift between two copies.
 *
 * The loader covers the retry gap too, so the states partition cleanly:
 * something is in flight, something failed, or there is a payload. `isLoading`
 * alone leaves a fourth — no data, no error, not loading — and a panel with
 * nothing in it is just a quieter way of saying "nothing found".
 */
export function AuditTrailTable({
  data,
  isLoading,
  error,
  page,
  onPageChange,
  onOpenDetail,
  emptyMessage = NO_AUDIT_ENTRIES_MATCH,
  badRequestHint,
}: {
  data: PagedResponse<AuditLogSummary> | undefined
  isLoading: boolean
  error: unknown
  /** 1-based. */
  page: number
  onPageChange: (page: number) => void
  onOpenDetail: (id: string) => void
  /** What an empty first page says. */
  emptyMessage?: string
  /** Shown under a 400's message, when the caller knows what in its request the server likely refused. */
  badRequestHint?: ReactNode
}) {
  const rows = data?.data ?? []
  const totalPages = data?.totalPages ?? 0
  const totalCount = data?.totalCount ?? 0

  return (
    <>
      {(isLoading || (!error && !data)) && <Center py="xl"><Loader /></Center>}

      {/*
        The first line only (errorSummary): a Development exception page arrives
        as plain text and can list the request's own headers further down. A 403
        never reaches here — the caller renders it as the restricted state.
      */}
      {error != null && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Failed to load audit log">
          {errorSummary(error) ?? 'An error occurred.'}
          {errorKind(error) === 'bad-request' && badRequestHint}
        </Alert>
      )}

      {/*
        `data` is required here, not just "not loading and not errored".

        Those two flags leave a gap: React Query retries, and BETWEEN attempts
        the state is `isLoading: false`, `error: null`, `data: undefined`, which
        both guards above pass. The table would then render from `data?.data ?? []`
        and report "No audit entries match the current filters" about a request
        that is in fact failing — observed live against a 401.

        On an audit trail that is the worst available lie. "Nothing matched" and
        "we were refused" are opposite findings, and the first is exactly what
        someone checking whether a permission change happened must never be told
        when the truth is that nobody asked successfully. An absent payload is
        not a measured zero.
      */}
      {!isLoading && error == null && data && (
        <>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Timestamp</Table.Th>
                <Table.Th>Entity Type</Table.Th>
                <Table.Th>Operation</Table.Th>
                <Table.Th>User</Table.Th>
                <Table.Th>Changed</Table.Th>
                <Table.Th w={60}>Details</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((log) => (
                <Table.Tr key={log.id}>
                  <Table.Td>{new Date(log.timestamp).toLocaleString()}</Table.Td>
                  <Table.Td>{log.entityType}</Table.Td>
                  <Table.Td>
                    <Badge color={operationColor(log.operation)}>{log.operation}</Badge>
                  </Table.Td>
                  <Table.Td>{log.userName || log.userId || '—'}</Table.Td>
                  <Table.Td>
                    {log.changedProperties.slice(0, 3).join(', ')}
                    {log.changedProperties.length > 3 && ' …'}
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label="View details">
                      <ActionIcon variant="subtle" onClick={() => onOpenDetail(log.id)}>
                        <IconEye size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          {/*
            A linked page can be past the end (`?page=9` on a trail that has since
            shrunk to two). That is an empty page of a non-empty result, and
            "no entries match" would be false.
          */}
          {rows.length === 0 && (
            <Center py="xl">
              {totalCount > 0 && page > 1 ? (
                <Stack align="center" gap="xs">
                  <Text c="dimmed">
                    Page {page} is past the end: these filters match {totalCount.toLocaleString()} entries
                    on {totalPages} {totalPages === 1 ? 'page' : 'pages'}.
                  </Text>
                  <Button variant="subtle" size="xs" onClick={() => onPageChange(1)}>Go to page 1</Button>
                </Stack>
              ) : (
                <Text c="dimmed">{emptyMessage}</Text>
              )}
            </Center>
          )}

          {totalPages > 1 && (
            <Group justify="space-between" mt="md">
              <Text size="sm" c="dimmed">
                Page {page} of {totalPages} ({totalCount.toLocaleString()} entries)
              </Text>
              <Pagination value={page} total={totalPages} onChange={onPageChange} />
            </Group>
          )}
        </>
      )}
    </>
  )
}
